import { defineBasicExtension } from "prosekit/basic"
import { createEditor, defineUpdateHandler, union, canUseRegexLookbehind } from "prosekit/core"
import { defineHighlight } from "prosekit/extensions/highlight"
import { defineMarkInputRule } from "prosekit/extensions/input-rule"

import { defineInputRule } from "prosekit/extensions/input-rule"
import { InputRule } from "prosekit/pm/inputrules"
import { emDash, openDoubleQuote, closeDoubleQuote, openSingleQuote, closeSingleQuote } from "prosekit/pm/inputrules"

/**
 * Converts three dots to an ellipsis character.
 */
function defineEllipsis() {
  const rule = new InputRule(/\.{3}$/, "…")
  return defineInputRule(rule)
}

/**
 * Converts left arrow ASCII to symbol
 */
function defineLeftArrow() {
  const rule = new InputRule(/<-$/, "←")
  return defineInputRule(rule)
}

/**
 * Converts right arrow ASCII to symbol
 */
function defineRightArrow() {
  const rule = new InputRule(/->$/, "→")
  return defineInputRule(rule)
}

/**
 * Converts left equals arrow ASCII to symbol
 */
function defineLeftEqualsArrow() {
  const rule = new InputRule(/<=$/, "⇐")
  return defineInputRule(rule)
}

/**
 * Converts right equals arrow ASCII to symbol
 */
function defineRightEqualsArrow() {
  const rule = new InputRule(/=>$/, "⇒")
  return defineInputRule(rule)
}

/**
 * Converts _this text_ to italics.
 */
function defineUnderscoredItalicInputRule() {
  return defineMarkInputRule({
    regex: new RegExp(
      (canUseRegexLookbehind() ? String.raw`(?<=\s|^)` : "") +
        String.raw`_([^\s_]|[^\s_][^_]*[^\s_])_$`,
    ),
    type: "italic",
  })
}

/**
 * Converts __this text__ to bold.
 */
function defineUnderscoredBoldInputRule() {
  return defineMarkInputRule({
    regex: new RegExp(
      (canUseRegexLookbehind() ? String.raw`(?<=\s|^)` : "") +
        String.raw`__([^\s_]|[^\s_][^_]*[^\s_])__$`,
    ),
    type: "bold",
  })
}

class JUDEProseElement extends HTMLElement {
  static {
    customElements.define("jude-prose", this)
  }

  connectedCallback() {
    this._firstBoot = true
    setTimeout(() => {
      const extension = union(
        defineBasicExtension(),
        defineHighlight(),
        defineUnderscoredItalicInputRule(),
        defineUnderscoredBoldInputRule(),
        defineEllipsis(),
        defineLeftArrow(),
        defineRightArrow(),
        defineLeftEqualsArrow(),
        defineRightEqualsArrow(),
        // define rules right out of ProseMirror:
        defineInputRule(emDash),
        defineInputRule(openDoubleQuote),
        defineInputRule(closeDoubleQuote),
        defineInputRule(openSingleQuote),
        defineInputRule(closeSingleQuote),
      )
      let contentHTML
      if (this.firstElementChild && this.firstElementChild.localName === "template") {
        contentHTML = this.firstElementChild.innerHTML
        this.firstElementChild.remove()
      } else {
        contentHTML = this.innerHTML
      }
      this.editor = createEditor({ extension, defaultContent: contentHTML })
      this.editor.use(defineUpdateHandler(() => this.save()))
      this.editor.mount(this)
      this.save() // first run will simply set initial state
      this._firstBoot = false
    })
  }

  save(force = false) {
    if (this._firstBoot) {
      this._previousHTML = this.editor.getDocHTML()
      return
    }

    const newHTML = this.editor.getDocHTML()
    if (force || newHTML != this._previousHTML) {
      this._previousHTML = newHTML
      const saveData = this.closest("jude-widget").querySelector("[widget-delegate")?.saveData || {}
      this.dispatchEvent(
        new CustomEvent("prose-update", { detail: { ...saveData, html: newHTML } }),
      )
      console.info(newHTML)
    }
  }

  disconnectedCallback() {
    this.editor.unmount()
  }
}
